/**
 * Combat systems (doc 05): tower firing per archetype — harpoon (pierce, crit,
 * bleed), arc (chains, stun, burn), mortar (flight, splash, bomblets,
 * knockback) — plus statuses and projectile resolution. Pure functions over a
 * narrow host, so the Sim class stays an orchestrator (doc 12 §Code health).
 */

import { CLOAK_SPLASH_MULT, CORE_REVEAL_RADIUS, DT, MIN_DAMAGE, TICK_RATE } from './const.ts';
import { towerStats, type TowerStats } from './defs.ts';
import { facingOctant } from './facing.ts';
import type { FlowField } from './flowfield.ts';
import type { SimGrid } from './grid.ts';
import type { Rng } from './rng.ts';
import { SPECIES } from './species.ts';
import type { Enemy, Projectile, SimEvent, SimState, Structure } from './types.ts';

/** What combat needs from the sim — nothing more. */
export interface CombatHost {
  state: SimState;
  grid: SimGrid;
  flow: FlowField;
  rng: Rng;
  emit(ev: SimEvent): void;
  byId(id: number): Structure | undefined;
  /** Damage an enemy and credit the source def with what landed (doc 08 XP). */
  /** Applies damage and returns what actually landed (doc 17 §7 numbers). */
  hurt(e: Enemy, raw: number, def: string): number;
  /**
   * How well a grid-powered tower is running: 0 off-grid, else the brownout
   * efficiency (doc 04 — everything throttles by the same fraction).
   */
  powerEff(structureId: number): number;
}

/**
 * Doc 03 armor rule + doc 05 armorIgnore: flat reduction, min 1 per hit.
 * Two doc 05 statuses ride on top: Reply-All shreds armour, and anything lit by
 * a Flashlight of Truth takes more from every source.
 *
 * The min-1 floor is per SHOT. A beam that pays it 30 times a second would deal
 * 30 dps of free damage through any armour, so continuous sources call
 * `beamDamage` below instead — it applies the same rules per second.
 */
function hitDamage(host: CombatHost, raw: number, e: Enemy, armorIgnore: number, aoe = false): number {
  const tick = host.state.tick;
  const armor = e.armorShredUntil > tick ? e.armor * (1 - e.armorShredPct) : e.armor;
  let amped = raw;
  if (e.litUntil > tick) amped *= 1 + e.litAmp;
  if (aoe && e.aoeUntil > tick) amped *= 1 + e.aoeAmp;
  return Math.max(MIN_DAMAGE, amped - armor * (1 - armorIgnore));
}

/** One tick of a continuous beam: armour and the floor apply per second. */
function beamDamage(host: CombatHost, dps: number, e: Enemy): number {
  return hitDamage(host, dps, e, 0) * DT;
}

export function applyStatuses(host: CombatHost, e: Enemy, st: TowerStats, src: string): void {
  const tick = host.state.tick;
  if (st.stun > 0) e.stunUntil = Math.max(e.stunUntil, tick + Math.round(st.stun * TICK_RATE));
  if (st.slowPct > 0 && st.slowDur > 0) {
    e.slowPct = Math.max(e.slowPct, st.slowPct);
    e.slowUntil = Math.max(e.slowUntil, tick + Math.round(st.slowDur * TICK_RATE));
  }
  if (st.bleedDps > 0) {
    e.bleedDps = Math.max(e.bleedDps, st.bleedDps);
    e.bleedUntil = tick + Math.round(st.bleedDur * TICK_RATE);
    e.bleedSlowPct = Math.max(e.bleedSlowPct, st.bleedSlow);
    e.bleedSpreadRadius = Math.max(e.bleedSpreadRadius, st.bleedSpreadRadius);
    e.bleedSrc = src;
  }
  if (st.burnDps > 0) {
    e.burnDps = Math.max(e.burnDps, st.burnDps);
    e.burnUntil = tick + Math.round(st.burnDur * TICK_RATE);
    e.burnSrc = src;
  }
}

/**
 * Doc 03/06 — the Cloaked rule, in one predicate: a Cloaked fish cannot be
 * TARGETED by anything unless something is revealing it, and a submerged
 * Leviathan is not in the water at all. Every acquisition path in this file
 * asks this before it looks at range.
 *
 * The reveal itself is written by `runRevealPass` (the Core and the Flashlight
 * of Truth) and by the Galvanic Surge; nothing here decides who can see.
 */
export function targetable(tick: number, e: Enemy): boolean {
  if (e.divingUntil > tick) return false;
  return !SPECIES[e.species]!.cloaked || e.revealedUntil > tick;
}

/**
 * The reveal sweep, one tick, before anything aims (docs 03/05/06).
 *
 * The Core lights radius 6 for free, and every powered Flashlight of Truth adds
 * its own circle. It deliberately runs OUTSIDE tower firing: a searchlight that
 * only works while it already has a target could never reveal the thing that is
 * hiding from it — the Laser would stare into an empty room forever.
 */
export function runRevealPass(host: CombatHost): void {
  const s = host.state;
  const hold = s.tick + 1; // re-written every tick a source covers the fish
  const mark = (cx: number, cy: number, radius: number, amp: number): void => {
    const r2 = radius * radius;
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.divingUntil > s.tick) continue;
      if ((e.x - cx) * (e.x - cx) + (e.y - cy) * (e.y - cy) > r2) continue;
      e.revealedUntil = Math.max(e.revealedUntil, hold);
      if (amp > 0) {
        e.litAmp = Math.max(e.litAmp, amp);
        e.litUntil = Math.max(e.litUntil, hold);
      }
    }
  };

  const core = host.byId(s.coreId);
  if (core && !core.ruin) {
    mark(core.i + (core.size - 1) / 2, core.j + (core.size - 1) / 2, CORE_REVEAL_RADIUS, 0);
  }
  for (const t of s.structures) {
    if (t.kind !== 'tower' || t.ruin || t.disabledUntil > s.tick) continue;
    const stats = towerStats(t.def, t.upgrades);
    if (stats.revealRadius <= 0) continue;
    if (host.powerEff(t.id) <= 0) continue; // an unplugged torch is a stick
    mark(t.i + (t.size - 1) / 2, t.j + (t.size - 1) / 2, stats.revealRadius, stats.litAmp);
  }
}

/** "First": in range, closest to the Core by flow distance, ties by id. */
function acquireTarget(host: CombatHost, cx: number, cy: number, range: number, minRange = 0): Enemy | undefined {
  const n = host.grid.size;
  let best: Enemy | undefined;
  let bestKey = Infinity;
  for (const e of host.state.enemies) {
    if (e.hp <= 0 || !targetable(host.state.tick, e)) continue;
    const dx = e.x - cx;
    const dy = e.y - cy;
    const d2 = dx * dx + dy * dy;
    if (d2 > range * range || d2 < minRange * minRange) continue;
    const ti = Math.max(0, Math.min(n - 1, Math.round(e.x)));
    const tj = Math.max(0, Math.min(n - 1, Math.round(e.y)));
    const key = host.flow.dist[host.grid.idx(ti, tj)]! * 1000 + e.id;
    if (key < bestKey) {
      bestKey = key;
      best = e;
    }
  }
  return best;
}

/** The Canceller's rule: the fattest thing in range, ties by id (doc 05 §6). */
function acquireFattest(host: CombatHost, cx: number, cy: number, range: number): Enemy | undefined {
  let best: Enemy | undefined;
  let bestHp = -1;
  for (const e of host.state.enemies) {
    if (e.hp <= 0 || !targetable(host.state.tick, e)) continue;
    const dx = e.x - cx;
    const dy = e.y - cy;
    if (dx * dx + dy * dy > range * range) continue;
    if (e.hp > bestHp || (e.hp === bestHp && best !== undefined && e.id < best.id)) {
      bestHp = e.hp;
      best = e;
    }
  }
  return best;
}

export function updateTowers(host: CombatHost, ): void {
  const s = host.state;
  for (const t of s.structures) {
    if (t.kind !== 'tower' || t.ruin) continue;
    if (t.cooldown > 0) t.cooldown = Math.max(0, t.cooldown - DT);
    if (t.disabledUntil > s.tick) {
      t.targetId = -1; // Snapjaw is hugging it
      continue;
    }
    const stats = towerStats(t.def, t.upgrades);
    const cx = t.i + (t.size - 1) / 2;
    const cy = t.j + (t.size - 1) / 2;

    // the Fan is not a gun: it works on everything in its field, every tick,
    // and never acquires a target (doc 05 §5)
    if (t.def === 'fan_enjoyer') {
      runFanField(host, t, cx, cy, stats);
      continue;
    }

    const target =
      stats.torpedoes > 0 && t.def === 'subscription_canceller'
        ? acquireFattest(host, cx, cy, stats.range)
        : acquireTarget(host, cx, cy, stats.range, stats.minRange);
    if (!target) {
      t.targetId = -1;
      // the beam cools down at the same speed it warmed up, unless the capstone
      // holds it ("Unblinking" keeps the ramp for `rampHold` seconds)
      if (t.ramp > 0) t.ramp = Math.max(0, t.ramp - (stats.rampHold > 0 ? DT / stats.rampHold : DT));
      continue;
    }
    t.targetId = target.id;
    t.facing = facingOctant(target.x - cx, target.y - cy);
    if (t.cooldown > 0) continue;
    // feed check: Stabby/Yeet eat ammo; Zappy drinks the capacitor (doc 05)
    if (stats.ammoPerShot > 0 && s.resources.ammo < stats.ammoPerShot) continue;
    if (stats.powerPerShot > 0 && s.capacitor < stats.powerPerShot) continue;
    s.resources.ammo -= stats.ammoPerShot;
    s.capacitor -= stats.powerPerShot;
    t.cooldown = 1 / stats.rate;
    t.firedAt = s.tick;

    if (t.def === 'zappy_boi') {
      fireChain(host, t.def, cx, cy, target, stats);
    } else if (t.def === 'yeet_cannon') {
      fireMortar(host, t.def, cx, cy, target, stats);
    } else if (t.def === 'laser_pointer') {
      fireBeam(host, t, cx, cy, target, stats);
    } else if (t.def === 'subscription_canceller') {
      fireTorpedoes(host, t.def, cx, cy, target, stats);
    } else {
      fireHarpoon(host, t, cx, cy, target, stats);
    }
  }
}

/** Stabby: hitscan spear; path A pierces along the line, path B crits, path C bleeds. */
function fireHarpoon(host: CombatHost, t: Structure, cx: number, cy: number, target: Enemy, stats: TowerStats): void {
  const dx = target.x - cx;
  const dy = target.y - cy;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  let victims: Enemy[];
  if (stats.pierce > 1) {
    // everyone within 0.7 tiles of the spear line, nearest first
    const hits: Array<{ e: Enemy; along: number }> = [];
    for (const e of host.state.enemies) {
      if (e.hp <= 0 || !targetable(host.state.tick, e)) continue;
      const px = e.x - cx;
      const py = e.y - cy;
      const along = px * ux + py * uy;
      if (along < 0 || along > stats.range) continue;
      const off = Math.abs(px * uy - py * ux);
      if (off <= 0.7) hits.push({ e, along });
    }
    hits.sort((a, b) => a.along - b.along || a.e.id - b.e.id);
    victims = hits.slice(0, stats.pierce).map((h) => h.e);
    if (victims.length === 0) victims = [target];
  } else {
    victims = [target];
  }
  const crit = stats.critChance > 0 && host.rng.next() < stats.critChance;
  for (const e of victims) {
    const dealt = host.hurt(
      e,
      hitDamage(host, stats.damage * (crit ? stats.critMult : 1), e, stats.armorIgnore),
      t.def,
    );
    applyStatuses(host, e, stats, t.def);
    host.emit({ type: 'enemyHit', enemyId: e.id, dmg: dealt, x: e.x, y: e.y, crit });
  }
  const last = victims[victims.length - 1]!;
  host.emit({ type: 'shot', towerId: t.id, x: cx, y: cy, hitX: last.x, hitY: last.y });
}

/** Zappy Boi: the arc hops to the nearest fresh enemy within 2.5 tiles. */
function fireChain(host: CombatHost, def: string, cx: number, cy: number, target: Enemy, stats: TowerStats): void {
  const CHAIN_HOP = 2.5; // dev-team constant, doc 13 §K
  const points = [{ x: cx, y: cy }];
  const hit = new Set<number>();
  let current: Enemy | undefined = target;
  let dmg = stats.damage;
  for (let k = 0; k < stats.chains && current; k++) {
    hit.add(current.id);
    const sp = SPECIES[current.species]!;
    const raw = dmg * (sp.swift ? stats.vsSwiftMult : 1);
    const dealt = host.hurt(current, hitDamage(host, raw, current, 0), def);
    applyStatuses(host, current, stats, def);
    points.push({ x: current.x, y: current.y });
    host.emit({ type: 'enemyHit', enemyId: current.id, dmg: dealt, x: current.x, y: current.y, crit: false });
    dmg *= 1 - stats.chainFalloff;
    let next: Enemy | undefined;
    let bestD = CHAIN_HOP * CHAIN_HOP;
    for (const e of host.state.enemies) {
      if (e.hp <= 0 || hit.has(e.id) || !targetable(host.state.tick, e)) continue;
      const d = (e.x - current.x) * (e.x - current.x) + (e.y - current.y) * (e.y - current.y);
      if (d < bestD) {
        bestD = d;
        next = e;
      }
    }
    current = next;
  }
  host.emit({ type: 'chain', points });
}

/** Yeet Cannon: a shell locks the target's CURRENT spot; walking out is legal. */
function fireMortar(host: CombatHost, def: string, cx: number, cy: number, target: Enemy, stats: TowerStats): void {
  const flight = Math.round(0.9 * TICK_RATE); // dev-team constant, doc 13 §K
  const s = host.state;
  s.projectiles.push({
    id: s.nextId++,
    srcDef: def,
    x: target.x,
    y: target.y,
    firedTick: s.tick,
    landTick: s.tick + flight,
    damage: stats.damage,
    radius: stats.splashRadius,
    bombletCount: stats.bombletCount,
    bombletDmg: stats.bombletDmg,
    bombletRadius: stats.bombletRadius,
    bombletStun: stats.bombletStun,
    knockback: stats.knockback,
    slowPct: stats.slowPct,
    slowDur: stats.slowDur,
    fromX: cx,
    fromY: cy,
  });
  host.emit({ type: 'launch', fromX: cx, fromY: cy, toX: target.x, toY: target.y, landTick: s.tick + flight });
}

export function resolveProjectiles(host: CombatHost, ): void {
  const s = host.state;
  if (s.projectiles.length === 0) return;
  const landing = s.projectiles.filter((p) => p.landTick <= s.tick);
  if (landing.length === 0) return;
  s.projectiles = s.projectiles.filter((p) => p.landTick > s.tick);
  for (const p of landing) {
    explode(host, p.srcDef, p.x, p.y, p.damage, p.radius, p, 0, p.splashDamage);
    for (let k = 0; k < p.bombletCount; k++) {
      const bx = p.x + host.rng.range(-p.radius, p.radius);
      const by = p.y + host.rng.range(-p.radius, p.radius);
      explode(host, p.srcDef, bx, by, p.bombletDmg, p.bombletRadius, null, p.bombletStun);
    }
  }
}

function explode(host: CombatHost,
  def: string,
  x: number,
  y: number,
  damage: number,
  radius: number,
  knock: Projectile | null,
  stunSeconds = 0,
  splashDamage?: number,
): void {
  const s = host.state;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (e.divingUntil > s.tick) continue; // submerged: the water took the blast
    const dx = e.x - x;
    const dy = e.y - y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > radius) continue;
    // a torpedo's splash is its own number: the bullseye takes the full hit,
    // the neighbours take `splashDamage` (doc 05 §6 Group Punishment)
    let raw = splashDamage !== undefined && d > 0.75 ? splashDamage : damage;
    // doc 03: splash finds a Cloaked fish, but only half of it does
    if (!targetable(s.tick, e)) raw *= CLOAK_SPLASH_MULT;
    const dealt = host.hurt(e, hitDamage(host, raw, e, 0, true), def);
    if (knock?.armorShredPct) {
      e.armorShredPct = Math.max(e.armorShredPct, knock.armorShredPct);
      e.armorShredUntil = s.tick + Math.round((knock.armorShredDur ?? 0) * TICK_RATE);
    }
    host.emit({ type: 'enemyHit', enemyId: e.id, dmg: dealt, x: e.x, y: e.y, crit: false });
    if (stunSeconds > 0) {
      e.stunUntil = Math.max(e.stunUntil, s.tick + Math.round(stunSeconds * TICK_RATE));
    }
    if (knock) {
      if (knock.knockback > 0 && !SPECIES[e.species]!.boss) {
        const len = d || 1;
        const nsize = host.grid.size - 1;
        e.x = Math.max(0, Math.min(nsize, e.x + (dx / len) * knock.knockback));
        e.y = Math.max(0, Math.min(nsize, e.y + (dy / len) * knock.knockback));
        e.attackTargetId = -1; // it lost its grip on whatever it was eating
      }
      if (knock.slowPct > 0) {
        e.slowPct = Math.max(e.slowPct, knock.slowPct);
        e.slowUntil = Math.max(e.slowUntil, s.tick + Math.round(knock.slowDur * TICK_RATE));
      }
    }
  }
  host.emit({ type: 'explosion', x, y, radius });
}

/**
 * LASER POINTER (doc 05 §4). A beam, not a shot: it burns `beamDps` every tick
 * it holds a target and ramps toward `rampMult` over `rampSeconds`. Path B
 * splits it across the nearest few targets; path C lights everything in the
 * reveal radius so every source hurts more.
 */
function fireBeam(host: CombatHost, t: Structure, cx: number, cy: number, target: Enemy, stats: TowerStats): void {
  const s = host.state;
  const eff = host.powerEff(t.id);
  if (eff <= 0) return; // unplugged: an expensive paperweight (doc 04)
  // the ramp only climbs while the SAME fish is being burned
  t.ramp = Math.min(stats.rampSeconds, t.ramp + DT);
  const ramped = 1 + (stats.rampMult - 1) * (t.ramp / stats.rampSeconds);
  const dps = stats.beamDps * ramped * eff;

  const victims: Enemy[] = [target];
  if (stats.beams > 1) {
    const others = host.state.enemies
      .filter((e) => e.hp > 0 && e.id !== target.id && targetable(s.tick, e))
      .map((e) => ({ e, d2: (e.x - cx) * (e.x - cx) + (e.y - cy) * (e.y - cy) }))
      .filter((o) => o.d2 <= stats.range * stats.range)
      .sort((a, b) => a.d2 - b.d2 || a.e.id - b.e.id);
    for (const o of others.slice(0, stats.beams - 1)) victims.push(o.e);
  }

  victims.forEach((e, k) => {
    const share = k === 0 ? dps : dps * stats.beamSplit;
    host.hurt(e, beamDamage(host, share, e), t.def);
    host.emit({ type: 'shot', towerId: t.id, x: cx, y: cy, hitX: e.x, hitY: e.y });
  });
  // the Flashlight of Truth does its revealing and lighting in `runRevealPass`,
  // every tick, whether or not this beam ever finds something to burn.
}

/**
 * FAN ENJOYER (doc 05 §5). No damage, no targeting, no ammo: a field that slows
 * everything inside it every tick, with an optional push pulse and an optional
 * vortex that drags the shoal into a pile for someone else's splash.
 */
function runFanField(host: CombatHost, t: Structure, cx: number, cy: number, stats: TowerStats): void {
  const s = host.state;
  const eff = host.powerEff(t.id);
  if (eff <= 0) return; // a fan with no watts is a sculpture
  const nsize = host.grid.size - 1;
  const hold = Math.round(0.3 * TICK_RATE); // re-applied every tick it stands
  const pushing = stats.pushEvery > 0 && s.tick % Math.round(stats.pushEvery * TICK_RATE) === 0;

  for (const e of s.enemies) {
    if (e.hp <= 0 || e.divingUntil > s.tick) continue;
    const dx = e.x - cx;
    const dy = e.y - cy;
    const d2 = dx * dx + dy * dy;
    const d = Math.sqrt(d2) || 1;

    if (d2 <= stats.fieldRadius * stats.fieldRadius && stats.fieldSlow > 0) {
      const swift = SPECIES[e.species]!.swift ? stats.swiftStrip : 0;
      e.slowPct = Math.max(e.slowPct, Math.min(0.9, (stats.fieldSlow + swift) * eff));
      e.slowUntil = Math.max(e.slowUntil, s.tick + hold);
      t.firedAt = s.tick; // the blades are visibly working
    }

    if (pushing && d2 <= stats.pushRadius * stats.pushRadius) {
      e.x = Math.max(0, Math.min(nsize, e.x + (dx / d) * stats.pushTiles));
      e.y = Math.max(0, Math.min(nsize, e.y + (dy / d) * stats.pushTiles));
      e.attackTargetId = -1; // it lost its grip on whatever it was chewing
      const dealt =
        stats.pushDamage > 0 ? host.hurt(e, hitDamage(host, stats.pushDamage, e, 0), t.def) : 0;
      host.emit({ type: 'enemyHit', enemyId: e.id, dmg: dealt, x: e.x, y: e.y, crit: false });
    }

    if (stats.pullRate > 0 && d2 <= stats.pullRadius * stats.pullRadius) {
      const step = Math.min(d, stats.pullRate * DT);
      e.x -= (dx / d) * step;
      e.y -= (dy / d) * step;
      if (stats.clusterAmp > 0) {
        e.aoeAmp = Math.max(e.aoeAmp, stats.clusterAmp);
        e.aoeUntil = s.tick + hold;
      }
    }
  }
  if (pushing) host.emit({ type: 'explosion', x: cx, y: cy, radius: stats.pushRadius });
}

/**
 * SUBSCRIPTION CANCELLER (doc 05 §6). Torpedoes at the fattest thing in range —
 * expensive per shot, and the only tower that scales INTO a boss instead of
 * away from it.
 */
function fireTorpedoes(host: CombatHost, def: string, cx: number, cy: number, target: Enemy, stats: TowerStats): void {
  const s = host.state;
  const flight = Math.round(0.5 * TICK_RATE);
  const boss = SPECIES[target.species]!.boss !== null;
  const damage = stats.damage * (boss ? stats.vsBossMult : 1);
  for (let k = 0; k < Math.max(1, stats.torpedoes); k++) {
    s.projectiles.push({
      id: s.nextId++,
      srcDef: def,
      x: target.x,
      y: target.y,
      firedTick: s.tick,
      landTick: s.tick + flight + k * 3, // a salvo arrives as a salvo, not a wall
      damage,
      radius: stats.splashRadius,
      bombletCount: 0,
      bombletDmg: 0,
      bombletRadius: 1,
      bombletStun: 0,
      knockback: 0,
      slowPct: 0,
      slowDur: 0,
      fromX: cx,
      fromY: cy,
      splashDamage: stats.splashFull > 0 ? damage : stats.splashDamage,
      armorShredPct: stats.armorShredPct,
      armorShredDur: stats.armorShredDur,
    });
    host.emit({ type: 'launch', fromX: cx, fromY: cy, toX: target.x, toY: target.y, landTick: s.tick + flight + k * 3 });
  }
}
